import React, { useState, useMemo } from 'react';
import { Table, Form, InputGroup, Button, Row, Col, Pagination } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import APIProviderFormModal from '../modals/APIProviderFormModal';

const APIProviderTable = ({ data, onDataChange }) => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    id: '',
    serviceName: '',
    providerName: '',
    providerType: '',
    status: '',
    credentials: ''
  });
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Column definitions
  const columns = [
    { key: 'actions', label: 'Actions' },
    { key: 'serviceName', label: 'Service Name' },
    { key: 'providerName', label: 'API Provider Name' },
    { key: 'providerType', label: 'Online/Offline Provider' },
    { key: 'status', label: 'Status' },
    { key: 'credentials', label: 'Credentials' }
  ];

  // Filter data based on search and column filters
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Column-wise filters
    Object.keys(columnFilters).forEach(key => {
      if (columnFilters[key]) {
        filtered = filtered.filter(row =>
          String(row[key]).toLowerCase().includes(columnFilters[key].toLowerCase())
        );
      }
    });

    // Date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.createdDate);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        return rowDate >= startDate && rowDate <= endDate;
      });
    }

    return filtered;
  }, [data, searchQuery, columnFilters, dateRange]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Handle column filter change
  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page
  };

  // Handle date range change
  const handleDateRangeChange = (type, value) => {
    setDateRange(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'API Providers');
    XLSX.writeFile(workbook, `API_Providers_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setColumnFilters({
      id: '',
      serviceName: '',
      providerName: '',
      providerType: '',
      status: '',
      credentials: ''
    });
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  // Handle Add Provider
  const handleAddProvider = () => {
    setModalMode('add');
    setSelectedProvider(null);
    setShowModal(true);
  };

  // Handle Edit Provider
  const handleEditProvider = (provider) => {
    setModalMode('edit');
    setSelectedProvider(provider);
    setShowModal(true);
  };

  // Handle Modal Close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProvider(null);
  };

  // Handle Save Provider (Add or Edit)
  const handleSaveProvider = (providerData) => {
    if (modalMode === 'add') {
      const newProvider = {
        ...providerData
      };
      
      // Call parent callback to update data
      if (onDataChange) {
        onDataChange([...data, newProvider]);
      }
    } else if (modalMode === 'edit') {
      // Update existing provider
      const updatedData = data.map(provider => 
        provider.id === providerData.id ? { ...provider, ...providerData } : provider
      );
      
      // Call parent callback to update data
      if (onDataChange) {
        onDataChange(updatedData);
      }
    }
  };

  // Pagination component
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  return (
    <div className="user-table-container">
      {/* Header with Title, Search, Add Provider, and Export */}
      <Row className="mb-3 g-2 align-items-center">
        <Col lg={3} md={4} sm={12}>
          <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
            API Providers
          </h4>
        </Col>
        <Col lg={9} md={8} sm={12} className="d-flex justify-content-end gap-2">
          <InputGroup style={{ maxWidth: '250px' }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
          <Button 
            size="sm"
            onClick={handleAddProvider}
            className="btn-outline-primary-custom"
            style={{ 
              padding: '0.4rem 1rem 0.5rem 1rem',
              fontSize: '0.875rem',
              height: 'auto'
            }}
          >
            <i className="bi bi-plus-circle me-1"></i> Add Provider
          </Button>
          <Button 
            size="sm" 
            variant="success" 
            onClick={handleExportExcel}
            style={{
              padding: '0.4rem 1rem 0.5rem 1rem',
              fontSize: '0.875rem',
              height: 'auto'
            }}
          >
            <i className="bi bi-file-earmark-excel me-1"></i> Excel
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
        <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
          <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, index) => (
                <tr key={index} style={{ background: '#f0f2f5' }}>
                  <td>
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      <Button 
                        size="sm" 
                        title="Edit"
                        onClick={() => handleEditProvider(row)}
                        className="btn-outline-primary-custom"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </div>
                  </td>
                  <td>{row.serviceName}</td>
                  <td style={{ fontWeight: '600', color: '#1e293b' }}>{row.providerName}</td>
                  <td>{row.providerType}</td>
                  <td>{row.status}</td>
                  <td style={{ fontSize: '0.85rem', color: '#666', maxWidth: '250px' }}>
                    <details>
                      <summary style={{ cursor: 'pointer', color: '#667eea', fontWeight: '600' }}>
                        View Credentials
                      </summary>
                      <div style={{ 
                        marginTop: '8px', 
                        padding: '8px', 
                        background: '#fff', 
                        borderRadius: '6px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                      }}>
                        {row.credentials}
                      </div>
                    </details>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                  <span className="text-muted">No API providers found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <Row className="align-items-center mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
        <Col lg={6} md={6} sm={12} className="mb-2 mb-md-0">
          <span style={{ 
            fontSize: '0.9rem', 
            color: '#64748b',
            fontWeight: '500'
          }}>
            Showing <strong style={{ color: '#667eea' }}>{startIndex + 1}</strong> to <strong style={{ color: '#667eea' }}>{Math.min(endIndex, filteredData.length)}</strong> of <strong style={{ color: '#667eea' }}>{filteredData.length}</strong> entries
          </span>
        </Col>
        <Col lg={6} md={6} sm={12} className="d-flex justify-content-end">
          <Pagination className="mb-0">{renderPagination()}</Pagination>
        </Col>
      </Row>

      {/* Add/Edit Provider Modal */}
      <APIProviderFormModal
        show={showModal}
        handleClose={handleCloseModal}
        mode={modalMode}
        providerData={selectedProvider}
        onSave={handleSaveProvider}
      />
    </div>
  );
};

export default APIProviderTable;

